import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from 'src/app/services/auth.service';
import { LanguageService } from 'src/app/services/language.service';
import { QuestionnaireService } from 'src/app/services/questionnaire.service';
import { Baseline, Question } from 'src/app/shared/models/questionnaire.model';
import { cloneDeep } from 'lodash';

@Component({
    selector: 'app-create-questionnaire-baseline',
    templateUrl: './create-questionnaire-baseline.component.html',
    styleUrl: './create-questionnaire-baseline.component.scss',
    standalone: false
})
export class CreateQuestionnaireBaselineComponent implements OnInit {
	@ViewChild('viewQuestionModal') viewQuestionModal!: TemplateRef<any>;
	@ViewChild('successModal') successModal!: TemplateRef<any>;

	steps = ['Baseline details', 'Baseline Questions', 'Summary'];
	today = Date.now();
	step = 1;
  currentStep = 0;
	submitted = false;
	spinner = false;
	editMode = false;

	success = false;
	successMessage = '';
	message = '';

	baseline_name!: string;
	description!: string;
	status: any;

	listStatus: any[] = [
		{ name: 'DRAFT', status_color: 'info'},
		{ name: 'ACTIVE', status_color: 'success'},
		{ name: 'PAUSED', status_color: 'warning'},
		{ name: 'CANCELED', status_color: 'danger'}
	];

	listLang: any[] = [];
	themeList: any[] = [];
	questions: Question[] = [];
	removeQuestions: any[] = [];
	selectedLanguage = 'en';
	questionIndex!: number;
	baseline_id: any;
	baseline!: Baseline;
	
	selectedQuestion!: Question;
	selectedQuestionIndex!: number;
	loading = true;

	constructor(private router: Router, private route: ActivatedRoute, private modalService: NgbModal, private questionnaireService: QuestionnaireService, private authService: AuthService, private languageService: LanguageService) {}

	ngOnInit(): void {
		this.loading = true;
		this.baseline_id = this.route.snapshot.paramMap.get('baseline_id');

		if (!this.baseline_id) {
			this.status = this.listStatus[1];
			this.getLists();
		} else {
			this.questionnaireService.getClientQuestionnaireBaseline(this.baseline_id).subscribe({
				next: (data: Baseline) => {
					this.baseline_name = data.name;
					this.status = this.listStatus.find(item => item.name === data.status);;
					this.selectedLanguage = data.lang_code;
					this.description = data.description
					this.editMode = true;

					this.questions = data.baseline_questions;
					this.getLists();
				},
				error: (err) => {
					this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting baseline');
				}
			});
		}
	}

	getLists() {
		this.listLang = this.languageService.listLang();
		this.questionnaireService.getGeneralList('questionnaire_theme').subscribe({
			next: (list) => {
				this.themeList = list.values;
				this.loading = false;
			},
			error: (err) => {
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Theme list');
			}
		});
	}

	backStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

	nextStep() {
		this.submitted = true;

		switch (this.currentStep) {
			case 0:
				if (this.checkBaselineDetails()) {
					this.currentStep++;
					this.submitted = false;
				}
				break;
			case 1:
				if (this.checkQuestionsValidity()) {
					this.currentStep++;
					this.submitted = false;
				}
				break;
		}
  }

	checkBaselineDetails(): boolean {
		if (!this.baseline_name || this.baseline_name === '' ) {
			return false;
		}

		if (!this.selectedLanguage){
			return false;
		}

		if (!this.status){
			return false;
		}

		return true;
	}

	viewQuestion(event: Event, question_index: number) {
		event.stopPropagation();
		this.selectedQuestionIndex = question_index;
		this.selectedQuestion = this.questions[question_index];

		this.modalService.open(this.viewQuestionModal, { size: 'xl', backdrop: 'static', keyboard : false });
	}

	checkQuestionsValidity(): boolean {
		let valid = true;

		this.questions.forEach(question => {
			if (question.status) {
				delete question.status;
			}

			if (!question.response.response_type) {
				valid = false;
				question.status = 'error';
			}

			if (question.body.question === '' || !question.body.question || question.body.question.length < 20) {
				valid = false;
				question.status = 'error';
			}

			if (question.time_limit < 0 || question.count_down < 0) {
				valid = false;
				question.status = 'error';
			}

			switch (question.response.response_type) {
				case 'SELECT':
					if (question.response.response_select) {
						if (question.response.response_select.response_options.length < 2) {
							valid = false;
							question.status = 'error';
						}
					}
					break;
				case 'RANGE':
					if (question.response.response_range) {
						if (question.response.response_range.range_min >= question.response.response_range.range_max) {
							valid = false;
							question.status = 'error';
						}
	
						if (!question.response.response_range.range_label_max || !question.response.response_range.range_label_min) {
							valid = false;
							question.status = 'error';
						}
					}
					break;
			}
		});

		return valid;
	}

	addQuestion() {
		const newQuestion: Question = {
			name: '',
			display_order: 0,
			time_limit: 30,
			count_down: 5,
			theme: null,
			threshold: null,
			body: {
				question: null
			},
			response: {
				response_type: null
			}
		}

		this.questions.push(newQuestion);
	}

	duplicateQuestion(event: Event, question: Question) {
		event.stopPropagation();

		const newQuestion: Question = cloneDeep(question)

		newQuestion.name = '';
    newQuestion.body.question = null;

		this.questions.push(newQuestion);
	}

	openDeleteQuestionModal(event: Event, content: any, index: number) {
		event.stopPropagation();

		this.questionIndex = index;
		this.modalService.open(content, { size: 'l', backdrop: 'static', keyboard : false });
	}

	deleteQuestion() {
		if (this.questions[this.questionIndex].id) {
			const id = this.questions[this.questionIndex].id;
			this.removeQuestions.push(id);
		}

		this.questions.splice(this.questionIndex, 1);
		this.modalService.dismissAll();
	}

	closeModal() {
		if (this.success) {
			this.editMode = false;
			this.router.navigate(['/baselines']);
			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}

	async saveBaseline() {
		await this.removeResponseChanges();

		const data = {
			details: {
				name: this.baseline_name,
				status: this.status.name,
				lang_code: this.selectedLanguage,
				description: this.description,
			},
			questions: this.questions,
			removeQuestions: this.removeQuestions
		}

		if (this.editMode) {
			this.questionnaireService.updateQuestionnaireBaseline(data, this.baseline_id).subscribe({
				next: () => {
					this.submitted = false;
					this.spinner = false;
					this.success = true;
					this.successMessage = 'Baseline saved successfully.';
	
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				},
				error: (err) => {
					this.submitted = false;
					this.spinner = false;
					this.success = false;
					this.successMessage = 'Baseline can not be saved!';
					this.message = err.message ? err.message : err;
	
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				}
			});
		} else {	
			this.questionnaireService.createQuestionnaireBaseline(data).subscribe({
				next: () => {
					this.submitted = false;
					this.spinner = false;
					this.success = true;
					this.successMessage = 'Baseline saved successfully.';
	
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				},
				error: (err) => {
					this.submitted = false;
					this.spinner = false;
					this.success = false;
					this.successMessage = 'Baseline can not be saved!';
					this.message = err.message ? err.message : err;
	
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				}
			});
		}
	}

	async removeResponseChanges() {
		for await (const question of this.questions) {
			switch (question.response.response_type) {
				case 'SELECT':
					if (question.response.response_range) {
						delete question.response.response_range;
					}

					if (question.response.response_voice) {
						delete question.response.response_voice;
					}
					break;
				case 'RANGE':
					if (question.response.response_select) {
						delete question.response.response_select;
					}

					if (question.response.response_voice) {
						delete question.response.response_voice;
					}
					break;
				case 'VOICE':
					if (question.response.response_select) {
						delete question.response.response_select;
					}

					if (question.response.response_range) {
						delete question.response.response_range;
					}
					break;
				case 'VISUAL':
					if (question.response.response_select) {
						delete question.response.response_select;
					}

					if (question.response.response_range) {
						delete question.response.response_range;
					}
					if (question.response.response_voice) {
						delete question.response.response_voice;
					}
					break;
			}
		}
	}
}